import React, { useState } from 'react';
import axios from 'axios';
import { toast, Toaster } from 'react-hot-toast';
import { RefreshCw, Send, RotateCcw, CheckCircle2, XCircle, Loader2, Copy, Check } from 'lucide-react';
import { useCurrentDomain } from './context/DomainContext';

const getCurrentEnv = (domain) => {
  const hostname = window.location.hostname.toLowerCase();

  if (hostname === "localhost") {
    //return "https://gybapiuat.greenyourbills.com/";
   return "https://gybdevapi-e5aqh0bsbfhhcjc5.eastus-01.azurewebsites.net/";
  }

  if (hostname.includes("devadmin") || hostname.includes("dev")) {
    //return "https://gybapiuat.greenyourbills.com/";
    return "https://gybdevapi-e5aqh0bsbfhhcjc5.eastus-01.azurewebsites.net/";
  }

  if ((domain || '').toLowerCase() === "ab.greenyourbills.com") {
    return "https://abdoctorapi2.greenyourbills.com/";
  }

  return "https://api.greenyourbills.com/";
};

// Bulk note generation is only permitted for these DBs
const ALLOWED_DOMAINS = [
  "greenyourbills.com",
  "ab.greenyourbills.com",
];

const isAllowedDomain = (domain) => ALLOWED_DOMAINS.includes((domain || '').trim().toLowerCase());

const CopyButton = ({ items, disabled }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    if (!items.length) return;
    navigator.clipboard.writeText(items.join(', ')).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <button
      onClick={handleCopy}
      disabled={disabled || !items.length}
      title="Copy to clipboard"
      style={{
        display: 'flex', alignItems: 'center', gap: 4,
        padding: '3px 10px', borderRadius: 6,
        border: '1.5px solid #d1d5db',
        background: copied ? '#f0fdf4' : '#fff',
        color: copied ? '#16a34a' : '#6b7280',
        fontSize: 12, fontWeight: 600, cursor: items.length ? 'pointer' : 'not-allowed',
        opacity: !items.length ? 0.4 : 1,
        transition: 'all 0.2s',
      }}
    >
      {copied ? <><Check size={12} /> Copied!</> : <><Copy size={12} /> Copy</>}
    </button>
  );
};

const BulkNoteRegenerate = () => {
  const [eventIdsInput, setEventIdsInput] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [currentEventId, setCurrentEventId] = useState('');
  const [processedCount, setProcessedCount] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [passList, setPassList] = useState([]);
  const [failList, setFailList] = useState([]);
  const [isDone, setIsDone] = useState(false);

  const { domain } = useCurrentDomain();
  const token = localStorage.getItem('token');
  const apiUrl = getCurrentEnv(domain);
  const isEnvAllowed = isAllowedDomain(domain);

  const parseEventIds = (input) => {
    if (!input || typeof input !== 'string') return [];
    return input.trim().split(',').map(id => id.trim()).filter(id => id.length > 0);
  };

  const handleSubmit = async () => {
    const rawInput = eventIdsInput.trim();
    if (!isEnvAllowed) { toast.error("Only greenyourbills.com and ab.greenyourbills.com DBs can be used for this feature"); return; }
    if (!rawInput) { toast.error("Please enter Event IDs"); return; }
    const eventIdList = parseEventIds(rawInput);
    if (eventIdList.length === 0) { toast.error("No valid Event IDs found"); return; }

    setIsProcessing(true);
    setIsDone(false);
    setCurrentEventId('');
    setPassList([]);
    setFailList([]);
    setProcessedCount(0);
    setTotalRecords(eventIdList.length);

    for (let i = 0; i < eventIdList.length; i++) {
      const eventId = eventIdList[i];
      setCurrentEventId(eventId);
      try {
        const response = await axios.get(
          `${apiUrl}api/Appointment/GenerateNote?EventID=${eventId}`,
          { headers: token ? { Authorization: `Bearer ${token}` } : {} }
        );
        const result = response.data;
        const isSuccess =
          (typeof result === 'string' && result.toLowerCase().includes('success')) ||
          result === '' || result === null || result === undefined;

        if (isSuccess) setPassList(prev => [...prev, eventId]);
        else setFailList(prev => [...prev, eventId]);
      } catch {
        setFailList(prev => [...prev, eventId]);
      }
      setProcessedCount(i + 1);
      await new Promise(resolve => setTimeout(resolve, 50));
    }

    setIsProcessing(false);
    setCurrentEventId('');
    setIsDone(true);
    toast.success("Bulk Note Regeneration Completed!");
  };

  const resetForm = () => {
    setEventIdsInput('');
    setCurrentEventId('');
    setPassList([]);
    setFailList([]);
    setProcessedCount(0);
    setTotalRecords(0);
    setIsProcessing(false);
    setIsDone(false);
  };

  const progress = totalRecords > 0 ? Math.round((processedCount / totalRecords) * 100) : 0;
  const hasResults = isDone || isProcessing;

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      width: '100%',
      padding: '0px',
      boxSizing: 'border-box',
      gap: 14,
      fontFamily: 'inherit',
    }}>
      <Toaster position="top-right" />

      {/* Input Card */}
      <div style={{
        background: '#fff',
        borderRadius: 12,
        border: '1px solid #e8f5ee',
        boxShadow: '0 2px 12px rgba(10,163,117,0.07)',
        overflow: 'hidden',
        flexShrink: 0,
      }}>
        <div style={{
          background: 'linear-gradient(135deg, #0aa375 0%, #07875f 100%)',
          padding: '8px 20px',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
        }}>
          <RefreshCw size={17} color="#fff" />
          <span style={{ color: '#fff', fontWeight: 600, fontSize: 14 }}>Bulk Note Regenerate</span>
        </div>

        <div style={{ padding: '16px 20px' }}>
          <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 8 }}>
            Event ID(s)
            <span style={{ fontWeight: 400, color: '#6b7280', marginLeft: 6 }}>— separate multiple IDs with commas</span>
          </label>
          <textarea
            rows={4}
            placeholder={"Single:   10795570\nMultiple: 10795570, 12345678, 99887766"}
            value={eventIdsInput}
            onChange={(e) => setEventIdsInput(e.target.value)}
            disabled={isProcessing}
            style={{
              width: '100%',
              borderRadius: 8,
              border: '1.5px solid #d1fae5',
              padding: '10px 14px',
              fontSize: 13,
              fontFamily: 'monospace',
              color: '#111827',
              background: isProcessing ? '#f9fafb' : '#f0fdf8',
              resize: 'vertical',
              outline: 'none',
              boxSizing: 'border-box',
              transition: 'border-color 0.2s',
            }}
            onFocus={e => e.target.style.borderColor = '#0aa375'}
            onBlur={e => e.target.style.borderColor = '#d1fae5'}
          />

          {!isEnvAllowed && (
            <div style={{
              marginTop: 10, padding: '8px 12px', borderRadius: 7,
              background: '#fef2f2', border: '1px solid #fecaca',
              color: '#dc2626', fontSize: 12, fontWeight: 600,
            }}>
              Bulk note generation is disabled for the selected DB ({domain}). Only greenyourbills.com and ab.greenyourbills.com DBs can be used for this feature.
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 12 }}>
            <button
              onClick={resetForm}
              disabled={isProcessing}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '7px 18px', borderRadius: 7, border: '1.5px solid #d1d5db',
                background: '#fff', color: '#374151', fontSize: 13, fontWeight: 600,
                cursor: isProcessing ? 'not-allowed' : 'pointer', opacity: isProcessing ? 0.5 : 1,
              }}
            >
              <RotateCcw size={14} /> Reset
            </button>
            <button
              onClick={handleSubmit}
              disabled={isProcessing || !eventIdsInput.trim() || !isEnvAllowed}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '7px 22px', borderRadius: 7, border: 'none',
                background: isProcessing || !eventIdsInput.trim() || !isEnvAllowed ? '#a7f3d0' : '#0aa375',
                color: '#fff', fontSize: 13, fontWeight: 600,
                cursor: isProcessing || !eventIdsInput.trim() || !isEnvAllowed ? 'not-allowed' : 'pointer',
                transition: 'background 0.2s',
              }}
            >
              {isProcessing
                ? <><Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> Processing...</>
                : <><Send size={14} /> Submit</>}
            </button>
          </div>
        </div>
      </div>

      {/* Progress + Results — grows to fill all remaining space */}
      {hasResults && (
        <div style={{
          flex: 1,
          minHeight: 0,
          background: '#fff',
          borderRadius: 12,
          border: '1px solid #e8f5ee',
          boxShadow: '0 2px 12px rgba(10,163,117,0.07)',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}>
          {/* Progress Bar */}
          <div style={{ padding: '14px 20px', borderBottom: '1px solid #f0fdf4', flexShrink: 0 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>
                {isDone
                  ? `✅ Completed — ${totalRecords} record${totalRecords !== 1 ? 's' : ''} processed`
                  : `Processing... ${processedCount} of ${totalRecords}`}
              </span>
              <span style={{ fontSize: 13, fontWeight: 700, color: '#0aa375' }}>{progress}%</span>
            </div>
            <div style={{ background: '#e5e7eb', borderRadius: 99, height: 8, overflow: 'hidden' }}>
              <div style={{
                height: '100%',
                width: `${progress}%`,
                background: 'linear-gradient(90deg, #0aa375, #34d399)',
                borderRadius: 99,
                transition: 'width 0.3s ease',
              }} />
            </div>
            {isProcessing && currentEventId && (
              <div style={{ marginTop: 6, fontSize: 12, color: '#6b7280' }}>
                Currently processing: <span style={{ fontFamily: 'monospace', color: '#0aa375', fontWeight: 600 }}>{currentEventId}</span>
              </div>
            )}
          </div>

          {/* Passed / Failed Columns — fill all remaining height and scroll independently */}
          <div style={{
            flex: 1,
            minHeight: 0,
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
          }}>
            {/* Passed */}
            <div style={{ borderRight: '1px solid #f0fdf4', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
              <div style={{
                padding: '10px 16px',
                borderBottom: '1px solid #f0fdf4',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                flexShrink: 0,
                background: '#f9fffe',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                  <CheckCircle2 size={15} color="#16a34a" />
                  <span style={{ fontSize: 13, fontWeight: 700, color: '#16a34a' }}>
                    Passed ({passList.length})
                  </span>
                </div>
                <CopyButton items={passList} disabled={isProcessing} />
              </div>
              <div style={{ flex: 1, overflowY: 'auto', padding: '8px 16px' }}>
                {passList.length === 0
                  ? <span style={{ fontSize: 12, color: '#9ca3af' }}>None yet</span>
                  : passList.map((id, i) => (
                    <div key={id} style={{
                      display: 'flex', alignItems: 'center', gap: 8,
                      padding: '6px 0',
                      borderBottom: i < passList.length - 1 ? '1px solid #f0fdf4' : 'none',
                    }}>
                      <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#22c55e', flexShrink: 0 }} />
                      <span style={{ fontFamily: 'monospace', fontSize: 13, color: '#15803d' }}>{id}</span>
                    </div>
                  ))
                }
              </div>
            </div>

            {/* Failed */}
            <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
              <div style={{
                padding: '10px 16px',
                borderBottom: '1px solid #fef2f2',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                flexShrink: 0,
                background: '#fffafa',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                  <XCircle size={15} color="#dc2626" />
                  <span style={{ fontSize: 13, fontWeight: 700, color: '#dc2626' }}>
                    Failed ({failList.length})
                  </span>
                </div>
                <CopyButton items={failList} disabled={isProcessing} />
              </div>
              <div style={{ flex: 1, overflowY: 'auto', padding: '8px 16px' }}>
                {failList.length === 0
                  ? <span style={{ fontSize: 12, color: '#9ca3af' }}>None</span>
                  : failList.map((id, i) => (
                    <div key={id} style={{
                      display: 'flex', alignItems: 'center', gap: 8,
                      padding: '6px 0',
                      borderBottom: i < failList.length - 1 ? '1px solid #fef2f2' : 'none',
                    }}>
                      <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#ef4444', flexShrink: 0 }} />
                      <span style={{ fontFamily: 'monospace', fontSize: 13, color: '#dc2626' }}>{id}</span>
                    </div>
                  ))
                }
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default BulkNoteRegenerate;